import { ComponentType, FormEvent, ReactElement, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AuthContext from "../../store/AuthContext";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import {
  getPublicWsgContact,
  submitPublicWsgContact,
  type PublicWsgContact,
  type PublicWsgContactStaff,
  type PublicWsgInstitution,
} from "../../Services/Person";
import { normalizeRoleToKey } from "../../Enums/UserEnums";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import LocationPin from "../../components/Atomos/Icons/LocationPin";
import Phone from "../../components/Atomos/Icons/ViewMode/Phone";
import Fax from "../../components/Atomos/Icons/ViewMode/Fax";
import Email from "../../components/Atomos/Icons/Email";
import Linkedin from "../../components/Atomos/Icons/ViewMode/Linkedin";
import XIcon from "../../components/Atomos/Icons/XCircled";
import Facebook from "../../components/Atomos/Icons/ViewMode/Facebook";
import Instagram from "../../components/Atomos/Icons/ViewMode/Instagram";
import Whatsapp from "../../components/Atomos/Icons/ViewMode/Whatsapp";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import { PhoneNumber, type IPhoneNumber } from "../../components/staff/PhoneNumber";
import chevronRightButton from "../../assets/Fonts/chevron-right-button.svg";
import watermark from "../../assets/images&icons/watermark-5.png";
import contactCardBackground from "../../assets/images&icons/bg-manageFirm.png";
import { publicStaffProfilePath } from "./publicStaffProfilePath";
import { toastError, toastSuccess } from "../../utils/toast";
import "../AboutUs/AboutUs.css";

type PublicContactSocial = {
  linkedinUrl?: string;
  xUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  whatsappUrl?: string;
};

const EMPTY_PHONE: IPhoneNumber = { code: "(+1)", phone: "", basecode: "USA" };
const SUCCESS_MESSAGE =
  "Your information was submitted successfully. Someone will be in touch shortly.";
const REQUIRED_MESSAGE = "Required";

type ContactFieldErrors = {
  topic?: string;
  name?: string;
  email?: string;
  firm?: string;
  phone?: string;
  comments?: string;
};

function isValidContactEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function contactSendError(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object" && "message" in data) {
    const message = String((data as { message?: unknown }).message ?? "").trim();
    if (message) {
      return message;
    }
  }
  return "Your message could not be sent. Please try again.";
}

const CONTACT_SOCIAL: PublicContactSocial = {
  linkedinUrl: "https://www.linkedin.com/company/worldservicesgroup",
  xUrl: "https://twitter.com/worldservsgroup",
  facebookUrl: "https://www.facebook.com/worldservicesgroup",
  instagramUrl: "https://www.instagram.com/worldservsgroup/",
  whatsappUrl: "https://wa.me/17136500333",
};

const SOCIAL_LINKS: Array<{
  key: keyof PublicContactSocial;
  label: string;
  Icon: ComponentType;
  disabled?: boolean;
}> = [
  { key: "linkedinUrl", label: "LinkedIn", Icon: Linkedin },
  { key: "xUrl", label: "X", Icon: XIcon },
  { key: "facebookUrl", label: "Facebook", Icon: Facebook },
  { key: "instagramUrl", label: "Instagram", Icon: Instagram },
  { key: "whatsappUrl", label: "WhatsApp", Icon: Whatsapp, disabled: true },
];

function ContactDetail({
  icon,
  children,
  weight = "medium",
}: {
  icon: ReactElement;
  children: ReactElement | string;
  weight?: "medium" | "normal";
}): ReactElement {
  const weightClass = weight === "normal" ? "font-normal" : "font-medium";
  return (
    <div className={`flex w-full min-w-0 items-start gap-3 font-roboto text-[20px] leading-normal text-white ${weightClass}`}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1 break-words">{children}</span>
    </div>
  );
}

function StaffCard({ person }: { person: PublicWsgContactStaff }): ReactElement {
  const profilePath = publicStaffProfilePath(person);
  const fullName = `${person.name} ${person.lastName}`.trim();
  const photoSrc = resolveProfilePictureDisplayUrl(person.id, undefined, {
    photoUpdatedAt: person.photoUpdatedAt,
  });
  const nameClass = "font-roboto text-[20px] font-semibold not-italic leading-normal text-primary";

  return (
    <div className="flex items-center gap-4">
      {profilePath ? (
        <Link
          to={profilePath}
          className="h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-full bg-gray-100"
        >
          <ProfilePhoto src={photoSrc} alt={fullName} />
        </Link>
      ) : (
        <div className="h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-full bg-gray-100">
          <ProfilePhoto src={photoSrc} alt={fullName} />
        </div>
      )}
      <div className="min-w-0">
        {profilePath ? (
          <Link to={profilePath} className={`${nameClass} hover:underline`}>
            {fullName}
          </Link>
        ) : (
          <p className={nameClass}>{fullName}</p>
        )}
        {person.title ? (
          <p className="m-0 font-roboto text-[16px] font-normal not-italic leading-[130%] text-gray-font">
            {person.title}
          </p>
        ) : null}
        {person.email ? (
          <a
            href={`mailto:${person.email}`}
            className="mt-4 flex items-center gap-2 font-roboto text-[14px] font-normal not-italic leading-[130%] text-gray-font hover:underline"
          >
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0070CD] text-white [&_svg]:h-3 [&_svg]:w-3">
              <Email />
            </span>
            {person.email}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function InstitutionDetails({
  institution,
}: {
  institution?: PublicWsgInstitution;
}): ReactElement | null {
  const addressLines = institution?.addressLines?.filter((line) => line.trim()) ?? [];
  const phone = institution?.phone?.trim() ?? "";
  const fax = institution?.fax?.trim() ?? "";
  const email = institution?.email?.trim() ?? "";
  if (!addressLines.length && !phone && !fax && !email) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {addressLines.length ? (
        <ContactDetail icon={<LocationPin className="h-8 w-8" />}>
          <>
            {addressLines.map((line, index) => (
              <span key={`${line}-${index}`}>
                {index > 0 ? <br /> : null}
                {line}
              </span>
            ))}
          </>
        </ContactDetail>
      ) : null}
      {phone ? (
        <ContactDetail icon={<span className="block h-8 w-8 [&_svg]:h-full [&_svg]:w-full"><Phone /></span>}>
          {phone}
        </ContactDetail>
      ) : null}
      {fax ? (
        <ContactDetail icon={<span className="block h-8 w-8 [&_svg]:h-full [&_svg]:w-full"><Fax /></span>}>
          {fax}
        </ContactDetail>
      ) : null}
      {email ? (
        <ContactDetail icon={<Email className="h-8 w-8 text-primary" />} weight="normal">
          <a className="underline" href={`mailto:${email}`}>
            {email}
          </a>
        </ContactDetail>
      ) : null}
    </div>
  );
}

function Contact(): ReactElement {
  const { loggedPerson, tokenDecoded } = useContext(AuthContext);
  const { pathname } = useLocation();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const [contact, setContact] = useState<PublicWsgContact | null>(null);
  const [failed, setFailed] = useState(false);
  const [topic, setTopic] = useState<string | undefined>(undefined);
  const [topicOptions, setTopicOptions] = useState<{ id: string; name: string }[]>([]);
  const [sending, setSending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [phone, setPhone] = useState<IPhoneNumber>(EMPTY_PHONE);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setPageMeta({
      title: "Contact Us",
      breadcrumbLabels: { [pathname]: "Contact Us" },
    });
    return () => clearPageMeta();
  }, [clearPageMeta, pathname, setPageMeta]);

  const roleKey = normalizeRoleToKey(
    tokenDecoded?.extension_WSG_Permissions || loggedPerson?.role,
  );
  const canEdit = roleKey === "wsgStaff" || roleKey === "wsgSuper";

  useEffect(() => {
    let cancelled = false;
    getPublicWsgContact()
      .then((data) => {
        if (!cancelled) {
          setContact(data);
          setTopicOptions(
            (data.topics ?? []).map((item) => ({ id: item.id, name: item.text })),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleStaff = contact?.staff ?? [];

  return (
    <div className="w-full bg-white z-10 flex flex-col white-page-layout-content">
      <section className="p-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-decimal text-3xl text-primary md:text-4xl">
            World Services Group
          </h2>
          {canEdit ? (
            <Link
              to="/site-updates/contacts"
              className="inline-flex items-center gap-2 self-start rounded bg-Default px-4 py-2 text-sm font-semibold text-white hover:bg-secondary"
            >
              Edit WSG Contact
              <Pencil width={16} height={16} color="#ffffff" />
            </Link>
          ) : null}
        </div>

        {failed ? (
          <p className="text-gray-font">Contact details are unavailable right now.</p>
        ) : (
          <div className="flex flex-col gap-10 self-stretch p-8 lg:flex-row lg:items-stretch">
            <div
              className="relative flex w-full max-w-[518px] shrink-0 flex-col self-stretch overflow-hidden bg-[#1b242c] bg-cover bg-center text-white"
              style={{ backgroundImage: `url(${contactCardBackground})` }}
            >
              <div className="absolute inset-0 bg-[#1F1F1F] opacity-50" />
              <div className="relative flex flex-1 flex-col justify-between px-6 pb-8 pt-10 [text-shadow:0_1px_1px_rgba(0,0,0,0.35)]">
                <div className="flex flex-col">
                <h3 className="mb-8 font-roboto text-[24px] font-bold leading-normal text-white">Contact Us!</h3>
                <InstitutionDetails institution={contact?.institution} />
                </div>
                <div>
                  <p className="mb-3 font-roboto text-base text-white">Our Social Media:</p>
                  <div className="flex items-center gap-4 text-white">
                    {SOCIAL_LINKS.map(({ key, label, Icon, disabled }) => {
                      const href = CONTACT_SOCIAL[key];
                      const iconClass = "block h-7 w-7 text-white [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-current";
                      if (disabled) {
                        return (
                          <span key={key} aria-label={label} className={iconClass}>
                            <Icon />
                          </span>
                        );
                      }
                      if (!href) {
                        return null;
                      }
                      return (
                        <a
                          key={key}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={label}
                          className={iconClass}
                        >
                          <Icon />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="my-8 h-px w-full shrink-0 bg-gray-300 lg:my-0 lg:h-auto lg:w-px"
              aria-hidden
            />

            <form
              ref={formRef}
              className="flex min-w-0 flex-1 flex-col gap-4"
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                const form = event.currentTarget;
                const fields = new FormData(form);
                const name = String(fields.get("name") ?? "").trim();
                const email = String(fields.get("email") ?? "").trim();
                const firm = String(fields.get("firm") ?? "").trim();
                const comments = String(fields.get("comments") ?? "").trim();
                const phoneNumber = [phone.code, phone.phone].filter(Boolean).join(" ").trim();
                const nextErrors: ContactFieldErrors = {};
                if (!topic?.trim()) {
                  nextErrors.topic = REQUIRED_MESSAGE;
                }
                if (!name) {
                  nextErrors.name = REQUIRED_MESSAGE;
                }
                if (!email) {
                  nextErrors.email = REQUIRED_MESSAGE;
                }
                else if (!isValidContactEmail(email)) {
                  nextErrors.email = "Enter a valid email address.";
                }
                if (!firm) {
                  nextErrors.firm = REQUIRED_MESSAGE;
                }
                if (!phone.phone.trim()) {
                  nextErrors.phone = REQUIRED_MESSAGE;
                }
                if (!comments) {
                  nextErrors.comments = REQUIRED_MESSAGE;
                }
                if (Object.keys(nextErrors).length > 0) {
                  setFieldErrors(nextErrors);
                  return;
                }
                setFieldErrors({});
                setSending(true);
                submitPublicWsgContact({
                  topic,
                  name,
                  email,
                  firm,
                  phone: phoneNumber,
                  comments,
                })
                  .then(() => {
                    form.reset();
                    setTopic(undefined);
                    setPhone(EMPTY_PHONE);
                    setFieldErrors({});
                    toastSuccess(SUCCESS_MESSAGE, { autoClose: 4000 });
                  })
                  .catch((error: unknown) => {
                    toastError(contactSendError(error), { autoClose: 4000 });
                  })
                  .finally(() => setSending(false));
              }}
            >
              <p className="text-sm text-gray-font">
                Please, select the topic below that best describes the nature of your message.
              </p>
              <DropDownList
                title=""
                name="topic"
                data={topicOptions}
                selectedObjst={topic}
                seter={(value) => {
                  setTopic(value);
                  setFieldErrors((current) => ({ ...current, topic: undefined }));
                }}
                placeholder="Select an option"
                disableInput
                internalSearch
                disableSort
                maxHeight="max-h-80"
                error={Boolean(fieldErrors.topic)}
                errormsj={fieldErrors.topic}
              />
              <div className="grid items-stretch gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-4">
                  <label className="flex flex-col gap-2 text-sm font-semibold text-gray-font">
                    <span>Your Name<span className="text-primary">*</span></span>
                    <input
                      className={`h-11 rounded border bg-gray-50 px-3 font-normal ${fieldErrors.name ? "contact-field-error border-primary" : "border-gray-200"}`}
                      name="name"
                      onChange={() => setFieldErrors((current) => ({ ...current, name: undefined }))}
                    />
                    {fieldErrors.name ? <span className="text-sm font-medium text-primary">{fieldErrors.name}</span> : null}
                  </label>
                  <label className="flex flex-col gap-2 text-sm font-semibold text-gray-font">
                    <span>Your Email Address<span className="text-primary">*</span></span>
                    <input
                      className={`contact-email-input h-11 w-full rounded border bg-gray-50 px-3 text-sm font-normal leading-5 ${fieldErrors.email ? "contact-field-error border-primary" : "border-gray-200"}`}
                      name="email"
                      type="email"
                      onChange={() => setFieldErrors((current) => ({ ...current, email: undefined }))}
                    />
                    {fieldErrors.email ? <span className="text-sm font-medium text-primary">{fieldErrors.email}</span> : null}
                  </label>
                  <label className="flex flex-col gap-2 text-sm font-semibold text-gray-font">
                    <span>Firm<span className="text-primary">*</span></span>
                    <input
                      className={`h-11 rounded border bg-gray-50 px-3 font-normal ${fieldErrors.firm ? "contact-field-error border-primary" : "border-gray-200"}`}
                      name="firm"
                      onChange={() => setFieldErrors((current) => ({ ...current, firm: undefined }))}
                    />
                    {fieldErrors.firm ? <span className="text-sm font-medium text-primary">{fieldErrors.firm}</span> : null}
                  </label>
                </div>
                <label className="flex h-full flex-col gap-2 text-sm font-semibold text-gray-font">
                  <span>Comments &amp; Questions<span className="text-primary">*</span></span>
                  <textarea
                    className={`min-h-[220px] flex-1 rounded border bg-gray-50 px-3 py-2 font-normal ${fieldErrors.comments ? "contact-field-error border-primary" : "border-gray-200"}`}
                    name="comments"
                    onChange={() => setFieldErrors((current) => ({ ...current, comments: undefined }))}
                  />
                  {fieldErrors.comments ? <span className="text-sm font-medium text-primary">{fieldErrors.comments}</span> : null}
                </label>
                <PhoneNumber
                  title="Phone Number"
                  required
                  Obj={phone}
                  seter={(value) => {
                    setPhone(value);
                    setFieldErrors((current) => ({ ...current, phone: undefined }));
                  }}
                  error={Boolean(fieldErrors.phone)}
                  errormsj={fieldErrors.phone}
                />
                <div className="flex w-full items-end">
                  <LegacyButton
                    text="Submit"
                    icon={
                      <img src={chevronRightButton} alt="" className="h-3 w-3" />
                    }
                    content="textIcon"
                    iconOrder="textFirst"
                    color="primary"
                    style="filled"
                    size="medium"
                    disabled={sending}
                    isLoading={sending}
                    className="!h-11 !min-h-0 w-full"
                    onClick={() => formRef.current?.requestSubmit()}
                  />
                </div>
              </div>
              <p className="text-sm text-primary">*Required Fields</p>
            </form>
          </div>
        )}

        <div className="relative mt-14">
          <img
            src={watermark}
            alt=""
            className="pointer-events-none absolute right-0 top-0 hidden w-64 opacity-20 lg:block"
          />
          <h2 className="mb-8 font-decimal text-3xl text-primary md:text-4xl">WSG Staff</h2>
          <div className="grid gap-x-8 gap-y-8 md:grid-cols-2 xl:grid-cols-3">
            {visibleStaff.map((person) => (
              <StaffCard key={person.id} person={person} />
            ))}
          </div>
        </div>

        <div className="mt-12 flex gap-2">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="h-6 w-6 rounded-full bg-primary" />
          ))}
        </div>
      </section>
    </div>
  );
}

export default Contact;
